/*
 * Copyright (C) 2019 - present by OpenGamma Inc. and the OpenGamma group of companies
 *
 * Please see distribution for license.
 */

import { tap, switchMap, map, catchError, withLatestFrom } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as userAuthActions from './user-auth.actions';
import { Action, select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { AuthService } from 'app/user-auth/services/auth.service';
import { observableOf, filterUndefined } from 'app/shared/rxjs/rxjs.utils';
import { SnackBarService } from 'app/shared/services/snack-bar-service';
import { State } from 'app/main/store';
import * as fromShared from 'app/user-auth/store/user-auth.selectors';
import { UserActionAuditService } from 'app/shared/services/user-action-audit.service';

@Injectable()
export class UserAuthEffects {
  /** Logs the login reason and sends off a tenant login request. */

  auditedLoginToTenant: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<userAuthActions.AuditedLoginToTenant>(userAuthActions.AUDITED_LOGIN_TO_TENANT),
      withLatestFrom(this.store.pipe(select(fromShared.getUserDetailsData), filterUndefined())),
      switchMap(([action, userDetails]) =>
        this.auditService
          .auditUserAction({
            organization: action.tenantDetails.organizationId,
            reason: action.auditDetails.requestReason,
            action: 'login',
            summary: `User ${userDetails.email} logging into tenant ${action.tenantDetails.organizationId}`,
            details: `Reason: ${action.auditDetails.requestReason}`
          })
          .pipe(
            map(
              () =>
                new userAuthActions.LoginToTenant(action.tenantDetails, action.previewEnvironment)
            ),
            catchError(err => observableOf(new userAuthActions.AuditedLoginToTenantError(err)))
          )
      )
    )
  );

  /** Login reason could not be logged. */

  auditedLoginSaveError: Observable<Action> = createEffect(
    () =>
      this.actions.pipe(
        ofType(userAuthActions.AUDITED_LOGIN_TO_TENANT_ERROR),
        tap((action: userAuthActions.AuditedLoginToTenantError) =>
          this.snackbarService.sendNotification({
            success: false,
            message: action.errorMessage
          })
        )
      ),
    { dispatch: false }
  );

  /** User logout handler. */

  logout: Observable<Action> = createEffect(
    () =>
      this.actions.pipe(
        ofType(userAuthActions.USER_LOGOUT),
        tap(() => this.authService.unauthenticate())
      ),
    { dispatch: false }
  );

  /** Logs into a tenant analytics environment */

  loginToTenant: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<userAuthActions.LoginToTenant>(userAuthActions.LOGIN_TO_TENANT),
      switchMap(action =>
        this.authService
          .loginToTenant(
            action.tenantDetails.clientId!,
            action.tenantDetails.organizationId,
            action.previewEnvironment
          )
          .pipe(
            map(() => new userAuthActions.LoginToTenantSuccess(action.tenantDetails)),
            catchError(() =>
              observableOf(new userAuthActions.LoginToTenantError(`Failed to login to tenant.`))
            )
          )
      )
    )
  );

  loginToTenantError: Observable<Action> = createEffect(
    () =>
      this.actions.pipe(
        ofType<userAuthActions.LoginToTenantError>(userAuthActions.LOGIN_TO_TENANT_ERROR),
        tap((action: userAuthActions.LoginToTenantError) => {
          this.snackbarService.sendNotification({ success: false, message: action.errorMessage });
        })
      ),
    { dispatch: false }
  );

  constructor(
    private store: Store<State>,
    private actions: Actions,
    private auditService: UserActionAuditService,
    private authService: AuthService,
    private snackbarService: SnackBarService
  ) {}
}
